import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { Transfer, ClientPayment } from '@/types';
import { calcTransfer, formatCurrency, generateId, exportToCSV, exportToPDF } from '@/utils/calculations';
// exportToPDF already uses printHtmlDocument internally
import StatusBadge from '@/components/base/StatusBadge';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
const emptyForm = (): Partial<Transfer> => ({
  date: new Date().toISOString().split('T')[0],
  clientId: '', clientName: '', transactionId: '', amount: 0,
  type: 'client_paid_me', status: 'pending', isSettled: false, notes: '',
});

type ActiveTab = 'all' | 'by_client';

export default function TransfersPage() {
  const {
    isDark, transfers, addTransfer, updateTransfer, deleteTransfer,
    clients, settings, clientPayments, addClientPayment, deleteClientPayment,
  } = useApp();

  const allTransfers = transfers;
  const allClients = clients;

  const [activeTab, setActiveTab] = useState<ActiveTab>('all');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Transfer | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Transfer>>(emptyForm());

  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTransfer, setPaymentTransfer] = useState<Transfer | null>(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentNotes, setPaymentNotes] = useState('');

  const [filters, setFilters] = useState({ search: '', status: '', type: '', dateFrom: '', dateTo: '' });
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sortField, setSortField] = useState<keyof Transfer>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const PER_PAGE = 15;

  // Helper: get total received for a transfer
  const getReceived = (transferId: string) =>
    clientPayments.filter(p => p.transferId === transferId).reduce((s, p) => s + p.amountReceived, 0);

  // Helper: get outstanding for a transfer
  const getOutstanding = (t: Transfer) => Math.max(0, t.netAmountDue - getReceived(t.id));

  // Helper: is fully received
  const isFullyReceived = (t: Transfer) => {
    const received = getReceived(t.id);
    return received >= t.netAmountDue && received > 0;
  };

  // Filtered transfers for "All" tab
  const filtered = useMemo(() => {
    let data = [...allTransfers];
    if (filters.search) data = data.filter(t =>
      t.clientName.toLowerCase().includes(filters.search.toLowerCase()) ||
      t.transactionId.toLowerCase().includes(filters.search.toLowerCase())
    );
    if (filters.status) data = data.filter(t => t.status === filters.status);
    if (filters.type) data = data.filter(t => t.type === filters.type);
    if (filters.dateFrom) data = data.filter(t => t.date >= filters.dateFrom);
    if (filters.dateTo) data = data.filter(t => t.date <= filters.dateTo);
    data.sort((a, b) => {
      const av = a[sortField] as string | number;
      const bv = b[sortField] as string | number;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return data;
  }, [allTransfers, filters, sortField, sortDir]);

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  // Per-client summary
  const clientSummaries = useMemo(() => {
    return allClients.map(c => {
      const clientTransfers = allTransfers.filter(t => t.clientId === c.id && t.status !== 'cancelled');
      const totalDue = clientTransfers.reduce((s, t) => s + t.netAmountDue, 0);
      const totalReceived = clientTransfers.reduce((s, t) => s + getReceived(t.id), 0);
      const outstanding = Math.max(0, totalDue - totalReceived);
      return { ...c, totalDue, totalReceived, outstanding, transferCount: clientTransfers.length };
    }).filter(c => c.transferCount > 0);
  }, [allClients, allTransfers, clientPayments]);

  // Transfers for selected client
  const clientTransfers = useMemo(() => {
    if (!selectedClientId) return [];
    return allTransfers
      .filter(t => t.clientId === selectedClientId)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [allTransfers, selectedClientId]);

  const selectedClient = allClients.find(c => c.id === selectedClientId);
  const selectedSummary = clientSummaries.find(c => c.id === selectedClientId);

  const openAdd = () => { setForm(emptyForm()); setEditItem(null); setShowModal(true); };
  const openEdit = (t: Transfer) => { setForm({ ...t }); setEditItem(t); setShowModal(true); };

  const openMarkReceived = (t: Transfer) => {
    const outstanding = getOutstanding(t);
    setPaymentTransfer(t);
    setPaymentAmount(outstanding);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentNotes('');
    setShowPaymentModal(true);
  };

  const handleFormChange = (field: keyof Transfer, value: unknown) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'clientId') {
        const c = allClients.find(cl => cl.id === value);
        if (c) updated.clientName = c.name;
      }
      if (['amount', 'type'].includes(field)) {
        const amt = field === 'amount' ? Number(value) : Number(prev.amount ?? 0);
        const typ = field === 'type' ? (value as Transfer['type']) : (prev.type ?? 'client_paid_me');
        const calc = calcTransfer(amt, typ, settings.myCommissionRate, settings.providerCommissionRate);
        return { ...updated, ...calc, myCommissionRate: settings.myCommissionRate, providerCommissionRate: settings.providerCommissionRate };
      }
      return updated;
    });
  };

  const handleSave = () => {
    if (!form.clientName || !form.amount || !form.date) return;
    const calc = calcTransfer(Number(form.amount), form.type ?? 'client_paid_me', settings.myCommissionRate, settings.providerCommissionRate);
    const transfer: Transfer = {
      id: editItem?.id ?? generateId(),
      date: form.date ?? '',
      clientId: form.clientId ?? '',
      clientName: form.clientName ?? '',
      transactionId: form.transactionId ?? `TXN-${Date.now()}`,
      amount: Number(form.amount),
      type: form.type ?? 'client_paid_me',
      myCommissionRate: settings.myCommissionRate,
      providerCommissionRate: settings.providerCommissionRate,
      status: form.status ?? 'pending',
      isSettled: form.isSettled ?? false,
      notes: form.notes ?? '',
      ...calc,
    };
    if (editItem) updateTransfer(transfer); else addTransfer(transfer);
    setShowModal(false);
  };

  const handleSavePayment = () => {
    if (!paymentTransfer || paymentAmount <= 0) return;
    const payment: ClientPayment = {
      id: generateId(),
      date: paymentDate,
      clientId: paymentTransfer.clientId,
      clientName: paymentTransfer.clientName,
      transferId: paymentTransfer.id,
      transferTxnId: paymentTransfer.transactionId,
      amountReceived: Number(paymentAmount),
      notes: paymentNotes,
    };
    addClientPayment(payment);
    setShowPaymentModal(false);
    setPaymentTransfer(null);
  };

  const handleExportCSV = () => exportToCSV(filtered.map(t => ({
    Date: t.date, Client: t.clientName, TxnID: t.transactionId,
    Amount: t.amount, Type: t.type, MyCommission: t.myCommission,
    ProviderCommission: t.providerCommission, Profit: t.myProfit,
    NetDue: t.netAmountDue, Received: getReceived(t.id),
    Outstanding: getOutstanding(t), Status: t.status,
  })), 'transfers');

  const handleExportPDF = () => exportToPDF(
    'Money Transfers',
    ['Date', 'Client', 'Txn ID', 'Amount', 'Net Due', 'Received', 'Outstanding', 'Status'],
    filtered.map(t => [
      t.date, t.clientName, t.transactionId,
      formatCurrency(t.amount), formatCurrency(t.netAmountDue),
      formatCurrency(getReceived(t.id)), formatCurrency(getOutstanding(t)), t.status,
    ])
  );

  const card = `rounded-xl border p-4 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`;
  const th = `text-xs font-semibold uppercase tracking-wide px-4 py-3 text-left cursor-pointer whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const td = `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const inp = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder:text-gray-500' : 'bg-gray-50 border-gray-200 text-gray-700 placeholder:text-gray-400'}`;

  // Overall summary
  const overallSummary = useMemo(() => {
    const totalDue = allTransfers.filter(t => t.status !== 'cancelled').reduce((s, t) => s + t.netAmountDue, 0);
    const totalReceived = clientPayments.reduce((s, p) => s + p.amountReceived, 0);
    const totalProviderDue = allTransfers.filter(t => t.status !== 'cancelled').reduce((s, t) => s + t.totalWithProviderCommission, 0);
    return { totalDue, totalReceived, outstanding: Math.max(0, totalDue - totalReceived), totalProviderDue };
  }, [allTransfers, clientPayments]);

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Money Transfers</h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{allTransfers.length} total records</p>
        </div>
        <div className="sm:ml-auto flex gap-2 flex-wrap">
          <button onClick={handleExportCSV} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-file-excel-line" /> CSV</button>
          <button onClick={handleExportPDF} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-file-pdf-line" /> PDF</button>
          <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap"><i className="ri-add-line" /> New Transfer</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className={`${card} border-l-4 border-l-amber-400`}>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Outstanding</p>
          <p className="text-xl font-bold mt-1 text-amber-500">{formatCurrency(overallSummary.outstanding)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Balance due from clients</p>
        </div>
        <div className={`${card} border-l-4 border-l-emerald-400`}>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Received</p>
          <p className="text-xl font-bold mt-1 text-emerald-500">{formatCurrency(overallSummary.totalReceived)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{clientPayments.length} payment(s) recorded</p>
        </div>
        <div className={`${card} border-l-4 border-l-teal-400`}>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Net Due</p>
          <p className="text-xl font-bold mt-1 text-teal-500">{formatCurrency(overallSummary.totalDue)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Across all active transfers</p>
        </div>
        <div className={`${card} border-l-4 border-l-violet-400`}>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Provider Due</p>
          <p className="text-xl font-bold mt-1 text-violet-500">{formatCurrency(overallSummary.totalProviderDue)}</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Principal + provider commission</p>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl w-fit ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        {([
          ['all', 'ri-list-check', 'All Transfers'],
          ['by_client', 'ri-user-line', 'By Client'],
        ] as const).map(([tab, icon, label]) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${activeTab === tab ? 'bg-emerald-500 text-white shadow-sm' : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <i className={icon} />{label}
          </button>
        ))}
      </div>

      {/* ===== ALL TRANSFERS TAB ===== */}
      {activeTab === 'all' && (
        <>
          {/* Filters */}
          <div className={card}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              <input placeholder="Search client / txn ID..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} className={inp} />
              <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className={`${inp} cursor-pointer`}>
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} className={`${inp} cursor-pointer`}>
                <option value="">All Types</option>
                <option value="client_paid_me">Client Paid Me</option>
                <option value="i_paid_client">I Paid Client</option>
              </select>
              <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} className={inp} />
              <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} className={inp} />
            </div>
          </div>

          {/* Table */}
          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                  <tr>
                    {(['date', 'clientName', 'transactionId', 'amount', 'type', 'myProfit', 'netAmountDue', 'status'] as (keyof Transfer)[]).map(f => (
                      <th key={f} className={th} onClick={() => { setSortField(f); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
                        {f === 'clientName' ? 'Client' : f === 'transactionId' ? 'Txn ID' : f === 'myProfit' ? 'Profit' : f === 'netAmountDue' ? 'Net Due' : f.charAt(0).toUpperCase() + f.slice(1)}
                        {sortField === f && <i className={`ml-1 ${sortDir === 'asc' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />}
                      </th>
                    ))}
                    <th className={th}>
                      <span className="flex items-center gap-1">
                        Provider Due
                        <span className={`text-xs px-1.5 py-0.5 rounded font-normal ${isDark ? 'bg-violet-900/40 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
                          Amt + {settings.providerCommissionRate}%
                        </span>
                      </span>
                    </th>
                    <th className={th}>Received</th>
                    <th className={th}>Outstanding</th>
                    <th className={th}>Payment</th>
                    <th className={th}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                  {paginated.map(t => {
                    const received = getReceived(t.id);
                    const outstanding = getOutstanding(t);
                    const fullyReceived = isFullyReceived(t);
                    return (
                      <tr key={t.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                        <td className={td}>{t.date}</td>
                        <td className={`${td} font-medium`}>{t.clientName}</td>
                        <td className={`${td} font-mono text-xs`}>{t.transactionId}</td>
                        <td className={`${td} font-semibold ${t.type === 'client_paid_me' ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(t.amount)}</td>
                        <td className={td}>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${t.type === 'client_paid_me' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                            {t.type === 'client_paid_me' ? 'Received' : 'Paid Out'}
                          </span>
                        </td>
                        <td className={`${td} font-semibold text-emerald-500`}>{formatCurrency(t.myProfit)}</td>
                        <td className={`${td} font-semibold`}>{formatCurrency(t.netAmountDue)}</td>
                        <td className={td}><StatusBadge status={t.status} /></td>
                        <td className={td}>
                          <span className="font-semibold text-violet-500">{formatCurrency(t.totalWithProviderCommission)}</span>
                          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {formatCurrency(t.amount)} + {formatCurrency(t.providerCommission)}
                          </p>
                        </td>
                        <td className={td}>
                          <span className={`text-sm font-semibold ${received > 0 ? 'text-emerald-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                            {formatCurrency(received)}
                          </span>
                        </td>
                        <td className={td}>
                          {fullyReceived ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                              <i className="ri-checkbox-circle-fill text-xs" /> Received
                            </span>
                          ) : (
                            <span className={`text-sm font-semibold ${outstanding > 0 ? 'text-amber-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                              {formatCurrency(outstanding)}
                            </span>
                          )}
                        </td>
                        <td className={td}>
                          {t.status !== 'cancelled' && !fullyReceived ? (
                            <button
                              onClick={() => openMarkReceived(t)}
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer whitespace-nowrap font-medium transition-colors"
                            >
                              <i className="ri-money-dollar-circle-line" /> Mark Received
                            </button>
                          ) : fullyReceived ? (
                            <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>—</span>
                          ) : null}
                        </td>
                        <td className={td}>
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEdit(t)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-teal-500 hover:bg-teal-50"><i className="ri-edit-line text-sm" /></button>
                            <button onClick={() => setDeleteId(t.id)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-red-400 hover:bg-red-50"><i className="ri-delete-bin-line text-sm" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {paginated.length === 0 && (
                    <tr><td colSpan={13} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No transfers found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? 'border-gray-800 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                <p className="text-xs">Page {page} of {totalPages} ({filtered.length} records)</p>
                <div className="flex gap-1">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded text-xs border cursor-pointer disabled:opacity-40">Prev</button>
                  <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded text-xs border cursor-pointer disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== BY CLIENT TAB ===== */}
      {activeTab === 'by_client' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Client List */}
          <div className="space-y-3">
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Select Client</h3>
            {clientSummaries.length === 0 && (
              <div className={`${card} text-center py-8`}>
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No clients with transfers yet</p>
              </div>
            )}
            {clientSummaries.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedClientId(c.id)}
                className={`w-full text-left rounded-xl border p-4 cursor-pointer transition-all ${
                  selectedClientId === c.id
                    ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                    : isDark ? 'bg-gray-900 border-gray-800 hover:border-gray-700' : 'bg-white border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.name}</p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{c.transferCount} transfer(s)</p>
                  </div>
                  {c.outstanding > 0 ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold whitespace-nowrap">
                      {formatCurrency(c.outstanding)} due
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                      Cleared
                    </span>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Due</p>
                    <p className={`text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{formatCurrency(c.totalDue)}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Received</p>
                    <p className="text-sm font-bold text-emerald-500">{formatCurrency(c.totalReceived)}</p>
                  </div>
                </div>
                {c.totalDue > 0 && (
                  <div className="mt-2">
                    <div className={`h-1.5 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className={`h-1.5 rounded-full transition-all ${c.outstanding === 0 ? 'bg-emerald-500' : 'bg-amber-400'}`}
                        style={{ width: `${Math.min(100, (c.totalReceived / c.totalDue) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Client Detail */}
          <div className="lg:col-span-2 space-y-4">
            {!selectedClientId ? (
              <div className={`${card} flex flex-col items-center justify-center py-16`}>
                <div className={`w-14 h-14 flex items-center justify-center rounded-full mb-3 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <i className={`ri-user-line text-2xl ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select a client to view their transfers</p>
              </div>
            ) : (
              <>
                {/* Client Header */}
                <div className={card}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedClient?.name}</h3>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedClient?.phone} · {selectedClient?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        const t = clientTransfers.find(t => !isFullyReceived(t) && t.status !== 'cancelled');
                        if (t) openMarkReceived(t);
                      }}
                      disabled={!clientTransfers.some(t => !isFullyReceived(t) && t.status !== 'cancelled')}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="ri-money-dollar-circle-line" /> Record Payment
                    </button>
                  </div>
                  {selectedSummary && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                      <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Net Due</p>
                        <p className={`text-base font-bold mt-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(selectedSummary.totalDue)}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Received</p>
                        <p className="text-base font-bold mt-0.5 text-emerald-500">{formatCurrency(selectedSummary.totalReceived)}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${selectedSummary.outstanding > 0 ? (isDark ? 'bg-amber-900/20' : 'bg-amber-50') : (isDark ? 'bg-gray-800' : 'bg-gray-50')}`}>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Outstanding</p>
                        <p className={`text-base font-bold mt-0.5 ${selectedSummary.outstanding > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                          {selectedSummary.outstanding > 0 ? formatCurrency(selectedSummary.outstanding) : 'Cleared'}
                        </p>
                      </div>
                      <div className={`rounded-lg p-3 ${isDark ? 'bg-violet-900/20' : 'bg-violet-50'}`}>
                        <p className={`text-xs ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>Provider Due</p>
                        <p className="text-base font-bold mt-0.5 text-violet-500">
                          {formatCurrency(
                            clientTransfers
                              .filter(t => t.status !== 'cancelled')
                              .reduce((s, t) => s + t.totalWithProviderCommission, 0)
                          )}
                        </p>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Amt + {settings.providerCommissionRate}% comm.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Client Transfers Table */}
                <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                  <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                    <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Transfer History</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                        <tr>
                          <th className={th}>Date</th>
                          <th className={th}>Txn ID</th>
                          <th className={th}>Amount</th>
                          <th className={th}>Net Due</th>
                          <th className={th}>Provider Due</th>
                          <th className={th}>Received</th>
                          <th className={th}>Outstanding</th>
                          <th className={th}>Status</th>
                          <th className={th}>Action</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                        {clientTransfers.map(t => {
                          const received = getReceived(t.id);
                          const outstanding = getOutstanding(t);
                          const fullyReceived = isFullyReceived(t);
                          const payments = clientPayments.filter(p => p.transferId === t.id);
                          return (
                            <ClientTransferRow
                              key={t.id}
                              t={t}
                              received={received}
                              outstanding={outstanding}
                              fullyReceived={fullyReceived}
                              payments={payments}
                              isDark={isDark}
                              td={td}
                              onMarkReceived={() => openMarkReceived(t)}
                              onDeletePayment={deleteClientPayment}
                              formatCurrency={formatCurrency}
                            />
                          );
                        })}
                        {clientTransfers.length === 0 && (
                          <tr><td colSpan={9} className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No transfers for this client</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* New/Edit Transfer Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'Edit Transfer' : 'New Transfer'} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Date *</label>
            <input type="date" value={form.date ?? ''} onChange={e => handleFormChange('date', e.target.value)} className={inp} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Client *</label>
            <select value={form.clientId ?? ''} onChange={e => handleFormChange('clientId', e.target.value)} className={`${inp} cursor-pointer`}>
              <option value="">Select client...</option>
              {allClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Transaction ID</label>
            <input value={form.transactionId ?? ''} onChange={e => handleFormChange('transactionId', e.target.value)} placeholder="TXN-001" className={inp} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Amount (CAD) *</label>
            <input type="number" min="0" value={form.amount ?? ''} onChange={e => handleFormChange('amount', e.target.value)} className={inp} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Transaction Type</label>
            <select value={form.type ?? 'client_paid_me'} onChange={e => handleFormChange('type', e.target.value)} className={`${inp} cursor-pointer`}>
              <option value="client_paid_me">Client Paid Me</option>
              <option value="i_paid_client">I Paid Client</option>
            </select>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Status</label>
            <select value={form.status ?? 'pending'} onChange={e => handleFormChange('status', e.target.value)} className={`${inp} cursor-pointer`}>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className={`sm:col-span-2 rounded-lg p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 ${isDark ? 'bg-gray-800' : 'bg-emerald-50'}`}>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>My Commission ({settings.myCommissionRate}%)</p><p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(form.myCommission ?? 0)}</p></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Provider Comm. ({settings.providerCommissionRate}%)</p><p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(form.providerCommission ?? 0)}</p></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>My Profit</p><p className="text-sm font-bold text-emerald-500">{formatCurrency(form.myProfit ?? 0)}</p></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Net Amount Due</p><p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(form.netAmountDue ?? 0)}</p></div>
            <div className={`col-span-2 sm:col-span-4 pt-3 mt-1 border-t ${isDark ? 'border-gray-700' : 'border-emerald-200'}`}>
              <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${isDark ? 'bg-violet-900/30 border border-violet-800' : 'bg-violet-50 border border-violet-200'}`}>
                <div>
                  <p className={`text-xs font-medium ${isDark ? 'text-violet-400' : 'text-violet-700'}`}>Provider Due Balance</p>
                  <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Principal ({formatCurrency(form.amount ?? 0)}) + Provider Commission ({formatCurrency(form.providerCommission ?? 0)})
                  </p>
                </div>
                <p className="text-lg font-bold text-violet-500">{formatCurrency(form.totalWithProviderCommission ?? 0)}</p>
              </div>
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Notes</label>
            <textarea value={form.notes ?? ''} onChange={e => handleFormChange('notes', e.target.value)} rows={2} className={`${inp} resize-none`} />
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-5">
          <button onClick={() => setShowModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
          <button onClick={handleSave} className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">Save Transfer</button>
        </div>
      </Modal>

      {/* Mark Payment Received Modal */}
      <Modal isOpen={showPaymentModal} onClose={() => { setShowPaymentModal(false); setPaymentTransfer(null); }} title="Mark Payment Received" size="md">
        {paymentTransfer && (() => {
          const received = getReceived(paymentTransfer.id);
          const outstanding = getOutstanding(paymentTransfer);
          return (
            <div className="space-y-4">
              <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-amber-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{paymentTransfer.clientName}</span>
                  <span className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{paymentTransfer.transactionId}</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Net Due</p>
                    <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(paymentTransfer.netAmountDue)}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Already Received</p>
                    <p className="text-sm font-bold text-emerald-500">{formatCurrency(received)}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Still Outstanding</p>
                    <p className="text-sm font-bold text-amber-500">{formatCurrency(outstanding)}</p>
                  </div>
                </div>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Payment Date *</label>
                <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} className={inp} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Amount Received (CAD) *</label>
                <input type="number" min="0" max={outstanding} value={paymentAmount} onChange={e => setPaymentAmount(Number(e.target.value))} className={inp} />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Max outstanding: {formatCurrency(outstanding)}</p>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Notes (optional)</label>
                <input value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} placeholder="e.g. Cash, bank transfer ref..." className={inp} />
              </div>
              {paymentAmount > 0 && (
                <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-800' : 'bg-emerald-50'}`}>
                  <p className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>After this payment:</p>
                  <p className={`text-sm font-bold mt-1 ${Math.max(0, outstanding - paymentAmount) === 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                    Remaining: {formatCurrency(Math.max(0, outstanding - paymentAmount))}
                    {Math.max(0, outstanding - paymentAmount) === 0 && ' — Fully Received!'}
                  </p>
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <button onClick={() => { setShowPaymentModal(false); setPaymentTransfer(null); }} className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button onClick={handleSavePayment} className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">Record Payment</button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => { if (deleteId) deleteTransfer(deleteId); setDeleteId(null); }} title="Delete Transfer" message="Are you sure you want to delete this transfer? This action cannot be undone." />
    </div>
  );
}

// Sub-component for client transfer row with expandable payment history
interface ClientTransferRowProps {
  t: Transfer;
  received: number;
  outstanding: number;
  fullyReceived: boolean;
  payments: ClientPayment[];
  isDark: boolean;
  td: string;
  onMarkReceived: () => void;
  onDeletePayment: (id: string) => void;
  formatCurrency: (n: number) => string;
}

function ClientTransferRow({ t, received, outstanding, fullyReceived, payments, isDark, td, onMarkReceived, onDeletePayment, formatCurrency }: ClientTransferRowProps) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
        <td className={td}>{t.date}</td>
        <td className={`${td} font-mono text-xs`}>{t.transactionId}</td>
        <td className={`${td} font-semibold ${t.type === 'client_paid_me' ? 'text-emerald-500' : 'text-red-500'}`}>{formatCurrency(t.amount)}</td>
        <td className={`${td} font-semibold`}>{formatCurrency(t.netAmountDue)}</td>
        <td className={td}>
          <span className="font-semibold text-violet-500">{formatCurrency(t.totalWithProviderCommission)}</span>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {formatCurrency(t.amount)} + {formatCurrency(t.providerCommission)}
          </p>
        </td>
        <td className={td}>
          <span className={`text-sm font-semibold ${received > 0 ? 'text-emerald-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            {formatCurrency(received)}
          </span>
        </td>
        <td className={td}>
          {fullyReceived ? (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
              <i className="ri-checkbox-circle-fill" /> Received
            </span>
          ) : (
            <span className={`text-sm font-semibold ${outstanding > 0 ? 'text-amber-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
              {formatCurrency(outstanding)}
            </span>
          )}
        </td>
        <td className={td}>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            t.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
            t.status === 'pending' ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-600'
          }`}>{t.status}</span>
        </td>
        <td className={td}>
          <div className="flex items-center gap-1">
            {t.status !== 'cancelled' && !fullyReceived && (
              <button
                onClick={onMarkReceived}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer whitespace-nowrap font-medium"
              >
                <i className="ri-money-dollar-circle-line" /> Receive
              </button>
            )}
            {payments.length > 0 && (
              <button
                onClick={() => setExpanded(e => !e)}
                className={`w-6 h-6 flex items-center justify-center rounded cursor-pointer ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <i className={`text-xs ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && payments.map(p => (
        <tr key={p.id} className={isDark ? 'bg-gray-800/40' : 'bg-emerald-50/50'}>
          <td className={`${td} text-xs pl-6`}>{p.date}</td>
          <td className={`${td} text-xs`} colSpan={3}>
            <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Payment received</span>
            {p.notes && <span className={`ml-2 italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>— {p.notes}</span>}
          </td>
          <td className={`${td} text-xs font-semibold text-emerald-500`}>{formatCurrency(p.amountReceived)}</td>
          <td className={td} colSpan={3} />
          <td className={td}>
            <button onClick={() => onDeletePayment(p.id)} className="w-6 h-6 flex items-center justify-center rounded cursor-pointer text-red-400 hover:bg-red-50">
              <i className="ri-delete-bin-line text-xs" />
            </button>
          </td>
        </tr>
      ))}
    </>
  );
}
